import {addYears, formatISO9075, subMonths} from 'date-fns'
import {envVariablesSchema, type RiskLevelConfig} from '~/types'

export const LIST = 'list'
export const GRID = 'grid'
export const LIST_TYPES = [GRID, LIST] as const
export type ListTypes = typeof LIST_TYPES
export type ListType = ListTypes[number]

export const COLOR_SCHEMES = ['light', 'dark'] as const
export type ColorSchemes = typeof COLOR_SCHEMES
export type ColorScheme = ColorSchemes[number]

export const getToday = () =>
  formatISO9075(new Date(), {representation: 'date'})
export const getStartDate = (months: number = 1) =>
  formatISO9075(subMonths(new Date(), months), {
    representation: 'date',
  })
export const getEndDate = getToday
export const getFarFutureDate = (years: number = 2) =>
  formatISO9075(addYears(new Date(), years), {
    representation: 'date',
  })

export const DEFAULT_LIMIT = 15

// PERMISSIONS
export const TERRORISM_TRACKER_READ = 'terrorism-tracker.read.no_trial'
export const TRM_READ = 'trm.read.no_trial'
export const RISK_MAPS_READ = 'risk-maps.read.no_trial'
export const DATA_MAPS_READ = 'data-maps.read.no_trial'
export const INTELLIGENCE_REPORTS_READ = 'intelligence-reports.read.no_trial'
export const DATA_PAGES_READ = 'data-pages.read.no_trial'
export const BRIEFING_SESSIONS_READ = 'briefing-sessions.read.no_trial'
export const COUNTRY_DOSSIER_READ = 'country-dossier.read.no_trial'

export const NOTIFICATIONS_READ = 'user.notifications.read'
export const NOTIFICATIONS_EDIT = 'user.notifications.edit'

export const CUSTOM_LOCATIONS_READ = 'client.custom_locations.read'
export const CUSTOM_LOCATIONS_EDIT = 'client.custom_locations.edit'

export const CLIENT_READ = 'user.client.read'
export const CLIENT_EDIT = 'user.client.edit'

export const USER_LICENSES_READ = 'user.licences.read'
export const USER_LICENSES_EDIT = 'user.licences.edit'

export const CLIENT_LICENSES_READ = 'client.licences.read'

export const CLIENT_LICENSES_EDIT = 'client.licences.edit'

export const USERS_READ = 'client.users.read'
export const USERS_EDIT = 'client.users.edit'

export const SUBSCRIPTIONS_READ = 'client.subscriptions.read'

// Redirect user based on their permissions in this order
const {DEFAULT_ROUTE, ENABLE_THREAT_EVENTS} = envVariablesSchema
  .pick({DEFAULT_ROUTE: true, ENABLE_THREAT_EVENTS: true})
  .parse(process.env)
export {ENABLE_THREAT_EVENTS}

export const THREAT_EVENTS_BASE_PATH = '/threat-events'
export const THREAT_EVENTS_HYBRID_PATH =
  '/threat-events?event_flags=hybrid_warfare'
export const THREAT_EVENTS_TERRORISM_PATH =
  '/threat-events?event_flags=incident&event_flags=plot'
export const TERRORISM_TRACKER_PATH = '/terrorism-tracker'
export const TERRORISM_TRACKER_MAP_PATH = '/terrorism-tracker/map'

export const serviceDisplayPriority = [
  {
    service: INTELLIGENCE_REPORTS_READ,
    href: DEFAULT_ROUTE,
  },
  {service: TERRORISM_TRACKER_READ, href: TERRORISM_TRACKER_PATH},
  {service: RISK_MAPS_READ, href: '/risk-ratings'},
  {service: TRM_READ, href: '/watchpoints'},
] as const

const legacyTerrorismTrackerNavigationItem = {
  label: 'TerrorismTracker',
  href: TERRORISM_TRACKER_PATH,
  services: [TERRORISM_TRACKER_READ],
  subNavigation: [],
} as const

const threatEventsNavigationItem = {
  label: 'Events',
  href: THREAT_EVENTS_BASE_PATH,
  services: [TERRORISM_TRACKER_READ],
  subNavigation: [
    {
      label: 'Hybrid',
      href: THREAT_EVENTS_HYBRID_PATH,
      services: [TERRORISM_TRACKER_READ],
    },
    {
      label: 'Terrorism',
      href: THREAT_EVENTS_TERRORISM_PATH,
      services: [TERRORISM_TRACKER_READ],
    },
  ],
} as const

export const getNavigation = (
  enableThreatEvents: boolean = ENABLE_THREAT_EVENTS,
) =>
  [
    {
      label: 'Dashboard',
      href: '/dashboard',
      services: [INTELLIGENCE_REPORTS_READ],
      subNavigation: [],
    },
    {
      label: 'Intelligence Reports',
      href: '/intelligence-reports',
      services: [INTELLIGENCE_REPORTS_READ],
      subNavigation: [
        {
          label: 'Intelligence Methodology',
          href: '/methodology',
          services: [RISK_MAPS_READ],
        },
      ],
    },
    {
      label: 'Data Visualisations',
      href: '/visual-analysis',
      services: [DATA_PAGES_READ],
      subNavigation: [],
    },
    // {
    //   label: 'Middle East Crisis Map',
    //   href: '/visual-analysis/1/full-screen-visualisation',
    //   subNavigation: [],
    // },
    {
      label: 'Risk Ratings Data',
      href: '/risk-ratings',
      services: [RISK_MAPS_READ],
      subNavigation: [
        {
          label: 'Risk Ratings Methodology',
          href: '/threat-and-risk',
          services: [RISK_MAPS_READ],
        },
      ],
    },
    {
      label: 'Country Dossiers',
      href: '/country-dossier',
      services: [COUNTRY_DOSSIER_READ],
      subNavigation: [],
    },
    {
      label: 'Watchpoints',
      href: '/watchpoints',
      services: [TRM_READ],
      subNavigation: [],
    },
    enableThreatEvents
      ? threatEventsNavigationItem
      : legacyTerrorismTrackerNavigationItem,
    {
      label: 'Briefing Sessions',
      href: '/briefing-sessions',
      services: [BRIEFING_SESSIONS_READ],
      subNavigation: [],
    },
    {
      label: 'About',
      href: '/about',
      subNavigation: [],
    },
    {
      label: 'Get in touch',
      href: '/get-in-touch',
      subNavigation: [],
    },
    {
      label: 'FAQ',
      href: '/faq',
      subNavigation: [],
    },
  ] as const

export const navigation = getNavigation()

export const RISKS_LEVELS: ReadonlyArray<RiskLevelConfig> = [
  {
    rating: 1,
    color: '#616267',
    textColor: 'white',
    name: 'Negligible',
  },
  {
    rating: 2,
    color: '#b4dfaa',
    textColor: 'black',
    name: 'Low',
  },
  {
    rating: 3,
    color: '#f6db6a',
    textColor: 'black',
    name: 'Moderate',
  },
  {
    rating: 4,
    color: '#f26432',
    textColor: 'black',
    name: 'High',
  },
  {
    rating: 5,
    color: '#e41522',
    textColor: 'white',
    name: 'Severe',
  },
  {
    rating: 6,
    color: '#9e0a18',
    textColor: 'white',
    name: 'Critical',
  },
] as const

export function getColorByRating(rating: number) {
  return RISKS_LEVELS.find((level) => level.rating === rating)?.color
}

export const WEAPON_TYPES = [
  'Vehicles',
  'Firearms',
  'Explosives',
  'Biological',
  'Chemical',
  'Grenades',
  'Incendiaries',
  'Melee weapons',
  'Missiles',
  'Mortars',
  'Radiological',
  'Rockets',
  'Undetermined',
  'UXO and Mines',
] as const

export type WeaponType = (typeof WEAPON_TYPES)[number]

type WeaponConfig = {
  type: (typeof WEAPON_TYPES)[number] // Ensure type is one of the weapon types
  color: string
  icon: string
}

export const WEAPON_CONFIG: WeaponConfig[] = [
  {type: 'Biological', color: '#47187C', icon: '/biological-icon.svg'},
  {type: 'Chemical', color: '#A35164', icon: '/chemical-icon.svg'},
  {type: 'Explosives', color: '#259FA8', icon: '/explosives-icon.svg'},
  {type: 'Firearms', color: '#446B27', icon: '/firearms-icon.svg'},
  {type: 'Grenades', color: '#C44927', icon: '/grenade-icon.svg'},
  {type: 'Incendiaries', color: '#373044', icon: '/incendiaries-icon.svg'},
  {type: 'Melee weapons', color: '#0D51BD', icon: '/melee-weapons-icon.svg'},
  {type: 'Missiles', color: '#8A6C1C', icon: '/missiles-icon.svg'},
  {type: 'Mortars', color: '#42686B', icon: '/mortars-icon.svg'},
  {
    type: 'Radiological',
    color: '#EB47F7',
    icon: '/radiological-icon.svg',
  },
  {type: 'Vehicles', color: '#3F5C2F', icon: '/vehicle-icon.svg'},
  {type: 'Rockets', color: '#E63946', icon: '/rocket-icon.svg'},
  {
    type: 'Undetermined',
    color: '#6C757D',
    icon: '/undetermined-icon.svg',
  },
  {
    type: 'UXO and Mines',
    color: '#D9A84E',
    icon: '/uxo-mine-icon.svg',
  },
]

export const getWeaponColorByType = (type: (typeof WEAPON_TYPES)[number]) => {
  return WEAPON_CONFIG.find((weapon) => weapon.type === type)?.color || '#ccc'
}

export const THREAT_EVENT_TYPE_CONFIG = [
  {
    type: 'Aerial/Maritime Incursion',
    key: 'aerial-maritime-incursion',
    color: '#3F5C2F',
    icon: '/threat-event-icons/aerial-maritime-incursion.svg',
  },
  {
    type: 'Armed Action',
    key: 'armed-action',
    color: '#446B27',
    icon: '/threat-event-icons/armed-action.svg',
  },
  {
    type: 'Arson',
    key: 'arson',
    color: '#373044',
    icon: '/threat-event-icons/arson.svg',
  },
  {
    type: 'Cyber Operation',
    key: 'cyber-operation',
    color: '#0D51BD',
    icon: '/threat-event-icons/cyber-operation.svg',
  },
  {
    type: 'Electromagnetic and Space Event',
    key: 'electromagnetic-and-space-event',
    color: '#EB47F7',
    icon: '/threat-event-icons/electromagnetic-and-space-event.svg',
  },
  {
    type: 'Explosive Attack',
    key: 'explosive-attack',
    color: '#259FA8',
    icon: '/threat-event-icons/explosive-attack.svg',
  },
  {
    type: 'Forcible Takeover',
    key: 'forcible-takeover',
    color: '#C44927',
    icon: '/threat-event-icons/forcible-takeover.svg',
  },
  {
    type: 'Hoax',
    key: 'hoax',
    color: '#6C757D',
    icon: '/threat-event-icons/hoax.svg',
  },
  {
    type: 'Human Intelligence Operation',
    key: 'human-intelligence-operation',
    color: '#42686B',
    icon: '/threat-event-icons/human-intelligence-operation.svg',
  },
  {
    type: 'Riots and Civil Unrest',
    key: 'riots-and-civil-unrest',
    color: '#A35164',
    icon: '/threat-event-icons/riots-and-civil-unrest.svg',
  },
  {
    type: 'Sanctions Circumvention',
    key: 'sanctions-circumvention',
    color: '#8A6C1C',
    icon: '/threat-event-icons/sanctions-circumvention.svg',
  },
  {
    type: 'Strikes, Demonstrations and Non-Violent Direct Action',
    key: 'strikes-demonstrations-and-non-violent-direct-action',
    color: '#D9A84E',
    icon: '/threat-event-icons/strikes-demonstrations-and-non-violent-direct-action.svg',
  },
  {
    type: 'Undetermined',
    key: 'undetermined',
    color: '#6C757D',
    icon: '/threat-event-icons/undetermined.svg',
  },
] as const

export const THREAT_EVENT_TYPES = THREAT_EVENT_TYPE_CONFIG.map(({type}) => type)

export const getThreatEventTypeColorByType = (type: string | null) => {
  return (
    THREAT_EVENT_TYPE_CONFIG.find((eventType) => eventType.type === type)
      ?.color || '#6C757D'
  )
}

export type MapTheme = 'streets' | 'satellite' | 'light' | 'dark' | 'terrain'
export const mapStyles = [
  {
    id: 'streets',
    name: 'Streets',
    value: 'mapbox://styles/mapbox/streets-v12',
  },
  {
    id: 'satellite',
    name: 'Satellite',
    value: 'mapbox://styles/mapbox/satellite-v9',
  },
  {
    id: 'light',
    name: 'Light',
    value: 'mapbox://styles/mapbox/light-v11',
  },
  {
    id: 'dark',
    name: 'Dark',
    value: 'mapbox://styles/mapbox/dark-v11',
  },
  {
    id: 'terrain',
    name: 'Terrain',
    value: 'mapbox://styles/mapbox/outdoors-v12',
  },
]

export const ENABLE_TRM_MAP = true

// DJ Metrics
export const DJ_METRICS_ENABLED = true
export const DJ_METRICS_URL =
  'https://api.int.dowjones.com/realusermetrics/metrics/user'
export const DJ_METRICS_URL_PERF =
  'https://api.int.dowjones.com/realusermetrics/metrics/product'
export const DJ_METRICS_VENDORJS =
  'https://analytics.int.dowjones.com/djin/1.651.0/dj-metrics.js'
export const DJ_METRICS_SCHEMA_VERSION = '2.0.0'
